import { alpha, Box, Typography } from '@mui/material';
import React from 'react';
import moment from 'moment-jalaali';
import 'moment/locale/fa';

import { NewsItemType } from 'features/news/news.types';

type NotificationItemProps = {
    notif: NewsItemType;
    index: number;
    isLastItem?: boolean;
    onOpen: (notification: NewsItemType) => void;
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
    notif,
    index,
    isLastItem,
    onOpen,
}) => {
    const notificationDate = React.useMemo(() => {
        moment.loadPersian({ dialect: 'persian-modern', usePersianDigits: true });
        return moment(notif?.updated_at || notif?.created_at)
            .locale('fa')
            .format('jD jMMMM jYYYY');
    }, [notif?.updated_at, notif?.created_at]);

    // read_news is resolved by the backend for the authenticated user.
    const isUnread = notif?.read_news !== true;
    const previewText =
        typeof notif?.text === 'string'
            ? notif.text.replace(/\s+/g, ' ').trim()
            : '';

    return (
        <Box
            key={index}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(notif)}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpen(notif);
                }
            }}
            sx={(theme) => {
                const unreadBackground = 'rgba(138, 114, 194, 0.09)';
                const unreadHover = 'rgba(138, 114, 194, 0.14)';
                const readBackground = 'transparent';
                const readHover =
                    theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.045)
                        : alpha(theme.palette.common.black, 0.035);

                return {
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.2,
                    px: 1.75,
                    py: 1.45,
                    minWidth: 0,
                    minHeight: 108,
                    cursor: 'pointer',
                    outline: 'none',
                    opacity: 1,
                    borderBottom: isLastItem ? 'none' : '1px solid',
                    borderColor:
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.075)
                            : alpha(theme.palette.common.black, 0.075),
                    backgroundColor: isUnread ? unreadBackground : readBackground,
                    transition: theme.transitions.create([
                        'background-color',
                        'border-color',
                        'opacity',
                    ]),
                    '&:hover, &:focus-visible': {
                        backgroundColor: isUnread ? unreadHover : readHover,
                    },
                };
            }}
        >
            <Box
                sx={(theme) => ({
                    flex: '0 0 auto',
                    width: 36,
                    height: 36,
                    mt: 0.15,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isUnread
                        ? theme.palette.mode === 'dark'
                            ? 'primary.light'
                            : 'primary.main'
                        : 'text.secondary',
                    backgroundColor: isUnread
                        ? alpha(
                              theme.palette.mode === 'dark'
                                  ? theme.palette.primary.light
                                  : theme.palette.primary.main,
                              theme.palette.mode === 'dark' ? 0.14 : 0.075,
                          )
                        : alpha(
                              theme.palette.text.secondary,
                              theme.palette.mode === 'dark' ? 0.075 : 0.045,
                          ),
                    border: '1px solid',
                    borderColor: isUnread
                        ? alpha(
                              theme.palette.mode === 'dark'
                                  ? theme.palette.primary.light
                                  : theme.palette.primary.main,
                              0.18,
                          )
                        : 'border.light',
                })}
            >
                <i
                    className="fa-light fa-bullhorn"
                    style={{ fontSize: 16 }}
                />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    className={isUnread ? 'font-yekan-heavy' : undefined}
                    sx={(theme) => ({
                        minWidth: 0,
                        fontSize: 13.25,
                        lineHeight: 1.8,
                        color: isUnread
                            ? 'text.primary'
                            : alpha(
                                  theme.palette.text.primary,
                                  theme.palette.mode === 'dark' ? 0.62 : 0.58,
                              ),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    })}
                >
                    {notif?.title}
                </Typography>

                {previewText ? (
                    <Typography
                        sx={(theme) => ({
                            mt: 0.4,
                            fontSize: 11.5,
                            lineHeight: 1.85,
                            color: isUnread
                                ? 'text.secondary'
                                : alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.72 : 0.68),
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                        })}
                    >
                        {previewText}
                    </Typography>
                ) : null}

                <Box
                    sx={{
                        mt: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    <Typography
                        color="text.light"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: 10.5,
                        }}
                    >
                        <i
                            className="fa-light fa-calendar-day"
                            style={{ fontSize: 10 }}
                        />
                        {notificationDate}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.45,
                            color: 'text.secondary',
                            opacity: isUnread ? 1 : 0.72,
                            fontSize: 10.5,
                        }}
                    >
                        {isUnread ? (
                            <>
                                <Typography sx={{ fontSize: 10.5, color: 'inherit' }}>
                                    مشاهده
                                </Typography>
                                <i
                                    className="fa-solid fa-chevron-left"
                                    style={{ fontSize: 8 }}
                                />
                            </>
                        ) : (
                            <>
                                <i
                                    className="fa-solid fa-check-double"
                                    style={{ fontSize: 9 }}
                                />
                                <Typography sx={{ fontSize: 10.5, color: 'inherit' }}>
                                    خوانده شده
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
